import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import type { FormInstance } from 'antd';
import dayjs from 'dayjs';
import type { F2130M, F2131, F2131Input, CreatePW2203Request } from '../../services/pw2203Api';
import pw2203Api from '../../services/pw2203Api';

export type Mode = 'browse' | 'add' | 'edit';

export const emptyMaster = (): Partial<F2130M> => ({
  f2130_01: '',
  f2130_11: null,
  f2130_18: 1,
  f2130_80: 'N',
  f2130_07: '',
  f2130_12: '',
  f2130_13: '',
  f2130_14: '',
  f2130_15: '',
  f2130_29: '',
  f2130_23: 0,
  f2130_25: 0,
  f2130_21: 0,
  f2130_30: '',
  f2130_31: '',
});

export const emptyDetail = (seq: number): F2131Input => ({
  f2131_02: seq,
  f2131_11: '',
  f2131_11a: '',
  f2131_12: '',
  f2131_13: 0,
  f2131_14: 0,
  f2131_15: 0,
  f2131_16: 0,
  f2131_18: '',
  f2131_21: '',
  f2131_22: 0,
});

export function populateFormFields(form: FormInstance, rec: F2130M) {
  form.setFieldsValue({
    f2130_11: rec.f2130_11 ? dayjs(rec.f2130_11) : null,
    f2130_07: rec.f2130_07 || '',
    f2130_12: rec.f2130_12 || '',
    f2130_13: rec.f2130_13 || '',
    f2130_14: rec.f2130_14 || '',
    f2130_15: rec.f2130_15 || '',
    f2130_29: rec.f2130_29 || '',
    f2130_18: rec.f2130_18 ?? 1,
    f2130_23: rec.f2130_23 ?? 0,
    f2130_25: rec.f2130_25 ?? 0,
    f2130_21: rec.f2130_21 ?? 0,
    f2130_30: rec.f2130_30 || '',
    f2130_31: rec.f2130_31 || '',
  });
}

export function toDetailInputs(details: F2131[]): F2131Input[] {
  return details.map(d => ({
    f2131_02: d.f2131_02,
    f2131_11: d.f2131_11,
    f2131_11a: d.f2131_11a,
    f2131_12: d.f2131_12,
    f2131_13: d.f2131_13,
    f2131_14: d.f2131_14,
    f2131_15: d.f2131_15,
    f2131_16: d.f2131_16,
    f2131_18: d.f2131_18,
    f2131_21: d.f2131_21,
    f2131_22: d.f2131_22,
  }));
}

export function usePW2203(form: FormInstance) {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F2130M[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [details, setDetails] = useState<F2131Input[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  const current = currentIdx >= 0 ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';
  const isApproved = current?.f2130_80 === 'P';

  const loadList = useCallback(async (kw?: string) => {
    setLoading(true);
    try {
      const res = kw ? await pw2203Api.search(kw) : await pw2203Api.list();
      setRecords(res.data);
      setTotal(res.total);
      if (res.data.length > 0) {
        setCurrentIdx(0);
        populateFormFields(form, res.data[0]);
        setDetails(toDetailInputs(res.data[0].details || []));
      } else {
        setCurrentIdx(-1);
        form.resetFields();
        setDetails([]);
      }
    } catch {
      message.error('載入資料失敗');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadList(); }, [loadList]);

  const handlePrev = () => {
    if (currentIdx > 0) {
      const idx = currentIdx - 1;
      setCurrentIdx(idx);
      populateFormFields(form, records[idx]);
      setDetails(toDetailInputs(records[idx].details || []));
    }
  };

  const handleNext = () => {
    if (currentIdx < records.length - 1) {
      const idx = currentIdx + 1;
      setCurrentIdx(idx);
      populateFormFields(form, records[idx]);
      setDetails(toDetailInputs(records[idx].details || []));
    }
  };

  const handleAdd = () => {
    setMode('add');
    form.setFieldsValue({ ...emptyMaster(), f2130_11: dayjs() });
    setDetails([emptyDetail(1)]);
  };

  const handleEdit = () => {
    if (!current) return;
    if (isApproved) { message.warning('已核准的單據不可修改'); return; }
    setMode('edit');
  };

  const handleCancel = () => {
    setMode('browse');
    if (current) { populateFormFields(form, current); setDetails(toDetailInputs(current.details || [])); }
    else { form.resetFields(); setDetails([]); }
  };

  const handleSave = async () => {
    let values: Record<string, unknown>;
    try {
      values = await form.validateFields();
    } catch {
      message.error('請修正表單錯誤');
      return;
    }

    const masterData: Partial<F2130M> = {
      f2130_11: values.f2130_11 ? (values.f2130_11 as dayjs.Dayjs).format('YYYY-MM-DDTHH:mm:ssZ') : null,
      f2130_07: values.f2130_07 as string,
      f2130_12: values.f2130_12 as string,
      f2130_13: values.f2130_13 as string,
      f2130_14: values.f2130_14 as string,
      f2130_15: values.f2130_15 as string,
      f2130_29: values.f2130_29 as string,
      f2130_18: values.f2130_18 as number,
      f2130_23: (values.f2130_23 as number) || 0,
      f2130_25: (values.f2130_25 as number) || 0,
      f2130_21: (values.f2130_21 as number) || 0,
      f2130_30: values.f2130_30 as string,
      f2130_31: (values.f2130_31 as string) || '',
    };

    const payload: CreatePW2203Request = { master: masterData, details };

    setLoading(true);
    try {
      if (mode === 'add') {
        const result = await pw2203Api.create(payload);
        message.success(`新增成功：${result.f2130_01}`);
        await loadList();
        const idx = records.findIndex(r => r.f2130_01 === result.f2130_01);
        if (idx >= 0) setCurrentIdx(idx);
      } else if (mode === 'edit' && current) {
        const result = await pw2203Api.update(current.f2130_01, payload);
        message.success('修改成功');
        const updated = records.map(r => r.f2130_01 === result.f2130_01 ? result : r);
        setRecords(updated);
        populateFormFields(form, result);
        setDetails(toDetailInputs(result.details || []));
      }
      setMode('browse');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { errors?: unknown[]; error?: string } } };
      if (error.response?.data?.errors) {
        message.error('驗證失敗，請檢查欄位');
      } else {
        message.error(error.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!current) return;
    if (isApproved) { message.warning('已核准的單據不可刪除'); return; }
    setLoading(true);
    try {
      await pw2203Api.delete(current.f2130_01);
      message.success('刪除成功');
      await loadList();
    } catch {
      message.error('刪除失敗');
    } finally {
      setLoading(false);
    }
  };

  const addDetailRow = () => {
    setDetails(prev => [...prev, emptyDetail(prev.length + 1)]);
  };

  const deleteDetailRow = (idx: number) => {
    setDetails(prev => prev.filter((_, i) => i !== idx).map((d, i) => ({ ...d, f2131_02: i + 1 })));
  };

  const updateDetail = (idx: number, field: keyof F2131Input, value: unknown) => {
    setDetails(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      if (field === 'f2131_13' || field === 'f2131_14') {
        const qty = field === 'f2131_13' ? (value as number) : next[idx].f2131_13;
        const price = field === 'f2131_14' ? (value as number) : next[idx].f2131_14;
        next[idx].f2131_15 = Math.round(qty * price * 100) / 100;
      }
      return next;
    });
  };

  return {
    mode, records, currentIdx, details, loading, total,
    current, isEditing, isApproved,
    loadList, handlePrev, handleNext,
    handleAdd, handleEdit, handleCancel, handleSave, handleDelete,
    addDetailRow, deleteDetailRow, updateDetail,
  };
}
